import { NgTemplateOutlet } from '@angular/common';
import { Component, computed } from '@angular/core';
import {
  applyWhen,
  disabled,
  FormField,
  FormRoot,
  RootFieldContext,
  SchemaPathTree,
  validate
} from '@angular/forms/signals';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TaskTimerComponent } from '@app/components/task/task-state-selectors/task-timer/task-timer.component';
import { TimeRegistrationDetailsComponent } from '@app/components/time-reports/time-report-dialog/time-registration-details/time-registration-details.component';
import { TimeReportDialogComponent } from '@app/components/time-reports/time-report-dialog/time-report-dialog.component';
import { TimeReportDialogService } from '@app/components/time-reports/time-report-dialog/time-report-dialog.service';
import {
  MANUAL_DURATION_KEYS,
  manualDurationsTotalSeconds,
  TimeReportFormModel
} from '@app/components/time-reports/time-report-dialog/time-report-form.types';
import { TimeReportFormComponent } from '@app/components/time-reports/time-report-dialog/time-report-form/time-report-form.component';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { TimeReportingType } from '@app/shared/types/models/shared/time-reporting-type';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiChoiceGroupComponent } from '@app/shared/ui/choice-group/ui-choice-group.component';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { convertHourToIsoTime } from '@app/shared/utils/time-utils';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-manual-hours-time-report-dialog',
  templateUrl: './manual-hours-time-report-dialog.component.html',
  providers: [TimeReportDialogService],
  imports: [
    NgTemplateOutlet,
    FormField,
    FormRoot,
    MatMenuModule,
    MatIconModule,
    TranslatePipe,
    UiChoiceGroupComponent,
    TimeReportFormComponent,
    TimeRegistrationDetailsComponent,
    TaskTimerComponent,
    UiDialogComponent,
    UiButtonComponent,
    UiTextareaComponent
  ]
})
export class ManualHoursTimeReportDialogComponent extends TimeReportDialogComponent {
  protected readonly isManualHoursType = computed(() => this.model().hourType === 'Manual');

  /**
   * `Manual` hours type: the per hour-type durations replace Manual / Range time entry and must sum up to more
   * than zero; any other hours type keeps the regular time entry and ignores the durations.
   */
  protected override extendSchema(path: SchemaPathTree<TimeReportFormModel>): void {
    const isManualHoursType = ({ valueOf }: Pick<RootFieldContext<unknown>, 'valueOf'>): boolean =>
      valueOf(path.hourType) === 'Manual';

    disabled(path.manualTime, { when: isManualHoursType });
    disabled(path.timeRange, { when: isManualHoursType });
    disabled(path.manualDurations, { when: context => !isManualHoursType(context) });
    applyWhen(path, isManualHoursType, manualHours =>
      validate(manualHours.manualDurations, ({ value }) =>
        manualDurationsTotalSeconds(value()) === 0
          ? { kind: 'invalidManualDurations', message: 'ui-forms-time-duration-is-required' }
          : undefined
      )
    );
  }

  protected override calculateDuration(): string | undefined {
    if (!this.isManualHoursType()) {
      return super.calculateDuration();
    }

    const total = this.sumManualDurationValues();
    return convertHourToIsoTime(total.hours, total.minutes);
  }

  protected override enrichReport(report: WorkTimeReport): void {
    if (report.hoursType !== 'Manual') {
      return;
    }

    const durations = this.model().manualDurations;
    report.dateFrom = undefined;
    report.dateTo = undefined;
    for (const key of MANUAL_DURATION_KEYS) {
      report[key] = this.isoDurationFromTimePicker(durations[key]);
    }
  }

  /** Leaving `Manual`: the durations' total becomes the Manual time of the regular entry. */
  protected onManualHoursTypeLeft(): void {
    const total = this.sumManualDurationValues();
    this.model.update(current => ({ ...current, manualTime: { hours: total.hours, minutes: total.minutes } }));
    this.selectedTimeReportingType.set(TimeReportingType.MANUAL);
  }

  private sumManualDurationValues(): TimePickerValue {
    const totalSeconds = manualDurationsTotalSeconds(this.model().manualDurations);

    return {
      hours: Math.floor(totalSeconds / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60)
    };
  }
}
